import { isValid, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import utcOffset from 'dayjs/plugin/utc';

dayjs.extend(utcOffset);

const MS_IN_DAY = 24 * 60 * 60 * 1000;

export const parseBookableDate = (value) => {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : parseISO(value);
  return isValid(d) ? d : null;
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse a CMS/event date as a local Date.
 * Date-only strings (YYYY-MM-DD) are treated as local midnight to avoid UTC day shifts.
 */
export const parseEventDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const dateOnly = String(value).match(DATE_ONLY);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

export const getMinSelectableDate = ({
  minBookableDate = null,
  minDate = null,
  isCurrentDaySelectable = false,
}) => {
  const cmsMin = parseBookableDate(minBookableDate);
  const dynamicMin = parseBookableDate(minDate);

  let base;
  if (dynamicMin) {
    base = dynamicMin;
  } else {
    base = isCurrentDaySelectable
      ? new Date()
      : new Date(Date.now() + MS_IN_DAY);
  }

  if (cmsMin) {
    return new Date(Math.max(base.getTime(), cmsMin.getTime()));
  }

  return base;
};

export const getMaxSelectableDate = ({
  maxBookableDate = null,
  maxNumDays = 365,
}) => {
  const maxFromDaysAhead = new Date(Date.now() + (maxNumDays * MS_IN_DAY));
  const cmsMax = parseBookableDate(maxBookableDate);
  return cmsMax
    ? new Date(Math.min(maxFromDaysAhead.getTime(), cmsMax.getTime()))
    : maxFromDaysAhead;
};

// TODO: convert this to use date-fns so we don't have 2 day
/**
 * Get an object with date information in several possible formats
 * @param {String} date
 * @returns {Object} Date data in several variations (long and short string)
 */
export const formatDate = (date) => {
  // TODO: Add date formats to our constants
  const d = dayjs(date, [
    'MM-DD-YYYY',
    'YYYY-MM-DD',
    'YYYY-MM-DD hh:mm a',
    'MM/DD/YYYY',
  ]);
  return {
    day: {
      num: d.format('D'),
      long: d.format('dddd'),
      short: d.format('ddd'),
    },
    year: d.year(),
    month: {
      long: d.format('MMMM'),
      short: d.format('MMM'),
      num: d.format('MM'),
    },
    time: {
      hours: d.format('h'),
      minutes: d.format('mm'),
      seconds: d.format('ss'),
      ampm: d.format('a'),
    },
  };
};

export const formatDateFromEpoch = (date) => {
  const d = date * 1000;

  return formatDate(d);
};

// TODO: combine with formatDate
export const formatDateUtc = (date) => {
  // TODO: Add date formats to our constants
  const d = dayjs(date, [
    'MM-DD-YYYY',
    'YYYY-MM-DD',
    'YYYY-MM-DD hh:mm a',
  ]).utc();
  return {
    milliseconds: d.valueOf(),
    day: {
      num: d.format('D'),
      long: d.format('dddd'),
      short: d.format('ddd'),
    },
    year: d.year(),
    month: {
      long: d.format('MMMM'),
      short: d.format('MMM'),
      num: d.format('MM'),
    },
    time: {
      hours: d.format('h'),
      minutes: d.format('mm'),
      ampm: d.format('a'),
    },
  };
};

// TODO: We may be able to clean this up at some point with ternaries rather than doing a bunch
// of if-else chaining
/**
 * Utility to help get all possible information from a start date string (and end date string
 * if it exists) into an object with formatted information
 * @param {String} startDate
 * @param {String} endDate
 * @param {String} type component type for handling variances in data formats
 * @returns {Object} Formatted date information
 */
export const getDateInfo = (startDate, endDate, type) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  let monthSe = '';
  let dayOfWeekSe = '';
  let daySe = '';
  let full = '';

  if (startDate && endDate && startDate !== endDate) {
    if (start.month.num === end.month.num) {
      monthSe = start.month.long;
    } else if (type === 'price') {
      monthSe = `${start.month.short} ${start.day.num} - ${end.month.short} ${end.day.num}`;
    } else {
      monthSe = `${start.month.short} - ${end.month.short}`;
    }

    if (start.day.num === end.day.num) {
      dayOfWeekSe = start.day.long;
    } else {
      dayOfWeekSe = `${start.day.short} - ${end.day.short}`;
    }

    if (start.day.num === end.day.num) {
      daySe = start.day.num;
    } else if (type === 'price') {
      if (start.month.num !== end.month.num) {
        daySe = '';
      } else {
        daySe = `${start.day.num}<span class="range-dash">-</span>${end.day.num}`;
      }
    } else {
      daySe = `${start.day.num}<span class="range-dash">-</span>${end.day.num}`;
    }

    if (start.month.num === end.month.num) {
      full = `${monthSe} ${daySe}, ${start.year}`;
    } else {
      full = `${monthSe}${daySe}, ${start.year}`;
    }

    return {
      isDateRange: true,
      dayOfWeek: dayOfWeekSe,
      year: `${start.year}-${end.year}`,
      month: monthSe,
      day: daySe,
      full,
    };
  }

  return {
    isDateRange: false,
    dayOfWeek: start.day.long,
    year: start.year,
    month: start.month.long,
    day: start.day.num,
    full: `${start.month.long} ${start.day.num}, ${start.year}`,
  };
};

export const getDateFromDatestampString = (s) => s.split('T')[0];

// convert number formatted as 1700 to 5:00 PM;
export const convertMilitaryTimeTo12Hour = (n) => {
  let digitStrings = [];
  if (n === 0 || n === 2400) return '12:00 AM';

  digitStrings = [...`${n}`]; // ["1", "2", "3", "4", "5"]
  let h = parseInt(digitStrings[0] + digitStrings[1], 10);
  const m = parseInt(digitStrings[2] + digitStrings[3], 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;

  return `${h}:${m} ${suffix}`;
};

export const checkSameStartEndDate = (
  startDate,
  endDate,
) => getDateFromDatestampString(startDate) === getDateFromDatestampString(endDate);
